<template>
  <div class="agentic-home-wrapper">
    <ErrorPopup />
    <!-- Main content area with blue gradient background -->
    <div class="home-content">
      <!-- Sidebar inside content -->
      <AgenticSidebar :sidebar-offset="sidebarOffset" />
      
      <!-- Main content section -->
      <div class="main-content-section" :class="{ 'without-chat': !isHomeScreen }">
        <!-- Upper section: Chat input box - Only on home screen -->
        <div v-if="isHomeScreen" class="chat-section">
          <div class="chat-prompt">
            <h1 class="chat-title">Let's create dashboards, {{ userName }} ?</h1>
          </div>
          <ChatInputBox @send-message="handleSendMessage" :loading="loadingDashboard" />
        </div>

        <!-- Lower section: Dashboard overview -->
        <DashboardOverview 
          :dashboards="filteredDashboards"
          :loading="loading"
          :title="overviewTitle"
          :description="overviewDescription"
          @dashboard-click="handleDashboardClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ChatInputBox from "@/components/AgenticDashboard/ChatInputBox.vue";
import DashboardOverview from "@/components/AgenticDashboard/DashboardOverview.vue";
import AgenticSidebar from "@/components/AgenticDashboard/AgenticSidebar.vue";
import ErrorPopup from "@/components/layout/Popups/ErrorPopup.vue";
import { mapGetters } from "vuex";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import screenWidthMixin from "@/assets/JS/screenWidthMixin";
import { eventBus } from "@/eventBus.js";

export default {
  name: "AgenticDashboardHome",
  mixins: [screenWidthMixin],
  components: {
    ChatInputBox,
    DashboardOverview,
    AgenticSidebar,
    ErrorPopup,
  },
  computed: {
    ...mapGetters("tenantId", ["userData", "getUser"]),
    userName() {
      if (this.getUser?.firstName) {
        return this.getUser.firstName;
      } else if (this.userData?.name) {
        return this.userData.name;
      }
      return "User";
    },
    tenantId() {
      return this.$route.path.split("/")[1] || getTenantPrefix();
    },
    isHomeScreen() {
      // Show chat only on home route (AgenticDashboard without dashboardId)
      return this.$route.name === "AgenticDashboard" && !this.$route.params.dashboardId;
    },
    overviewTitle() {
      const routeName = this.$route.name;
      if (routeName === "AgenticDashboard" && !this.$route.params.dashboardId) {
        return "Home";
      } else if (routeName === "AgenticDashboardAll") {
        return "All dashboards";
      } else if (routeName === "AgenticDashboardStarred") {
        return "Starred";
      } else if (routeName === "AgenticDashboardShared") {
        return "Shared with me";
      }
      return "Dashboards";
    },
    overviewDescription() {
      const routeName = this.$route.name;
      if (routeName === "AgenticDashboard" && !this.$route.params.dashboardId) {
        return "Here you can view and manage your existing dashboards";
      } else if (routeName === "AgenticDashboardAll") {
        return "View all your dashboards including private and public";
      } else if (routeName === "AgenticDashboardStarred") {
        return "Your starred and default dashboards";
      } else if (routeName === "AgenticDashboardShared") {
        return "Dashboards shared with you by others";
      }
      return "Here you can view and manage your existing dashboards";
    },
    sidebarOffset() {
      // Calculate left offset based on main sidebar state
      // When closed: 64px, when open: 280px
      // Add 8px gap after the main sidebar
      const baseOffset = this.mainSidebarOpen ? 280 : 64;
      return baseOffset + 8;
    },
    filteredDashboards() {
      if (!this.allDashboards || this.allDashboards.length === 0) {
        return [];
      }

      const routeName = this.$route.name;
      const userEmail = this.getUser?.username;

      let filtered = [];

      // Check if we're on home route (AgenticDashboard without dashboardId)
      const isHomeRoute = routeName === "AgenticDashboard" && !this.$route.params.dashboardId;

      if (isHomeRoute) {
        // Home: First 8 dashboards
        filtered = this.allDashboards.slice(0, 8);
      } else if (routeName === "AgenticDashboardAll") {
        // All: All dashboards (private and public)
        filtered = this.allDashboards;
      } else if (routeName === "AgenticDashboardStarred") {
        // Starred: Dashboards with isDefault or isStarred
        filtered = this.allDashboards.filter(
          (dashboard) => dashboard.isDefault || dashboard.isStarred
        );
      } else if (routeName === "AgenticDashboardShared") {
        // Shared: Public dashboards or shared dashboards (not created by current user)
        filtered = this.allDashboards.filter(
          (dashboard) =>
            dashboard.AccessLevel === "public" ||
            (dashboard.AccessLevel === "shared" && dashboard.createdBy !== userEmail)
        );
      } else {
        // Default to first 8
        filtered = this.allDashboards.slice(0, 8);
      }

      return filtered.map((dashboard) => ({
        id: dashboard.ID,
        title: dashboard.name,
        charts: dashboard.Charts ? dashboard.Charts.length : 0,
        totalCharts: 6,
        isStarred: dashboard.isDefault || dashboard.isStarred || false,
        previewImages: dashboard.Charts
          ? dashboard.Charts.slice(0, 4).map(() => "/path/to/chart.png")
          : [],
        dashboard: dashboard, // Keep original dashboard data
      }));
    },
  },
  data() {
    return {
      allDashboards: [],
      loading: false,
      loadingDashboard: false, // Loading state for dashboard creation
      mainSidebarOpen: false, // Track main sidebar state
      sidebarObserver: null, // MutationObserver for sidebar state
    };
  },
  methods: {
    async handleSendMessage(message) {
      // Call the LLM API to create dashboard
      const llmUrl = process.env.VUE_APP_LLM_URL;
      if (!llmUrl) {
        console.error("VUE_APP_LLM_URL is not defined in .env");
        this.$store.commit('api/showSnackbar', {
          message: "LLM URL is not configured",
          color: 'error',
          timeout: 5000
        });
        return;
      }

      this.loadingDashboard = true;
      try {
        const response = await api.post(
          `${llmUrl}api/dashboard`,
          { message: message },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "X-TenantID": this.tenantId,
            },
          }
        );

        if (response && response.data) {
          const dashboardData = response.data;
          // Show success message
          this.$store.commit('api/showSnackbar', {
            message: "Dashboard created successfully",
            color: 'success',
            timeout: 3000
          });
          // Navigate to dashboard view with the dashboard name
          // Use the name directly - Vue Router will handle encoding automatically
          const dashboardName = dashboardData.Name || dashboardData.name || "Untitled Dashboard";
          this.$router.push({
            name: "AgenticDashboardView",
            params: { dashboardName: dashboardName },
            query: { 
              message: message,
              dashboardData: JSON.stringify(dashboardData)
            },
          });
        } else {
          console.error("Invalid response from API");
          this.$store.commit('api/showSnackbar', {
            message: "Failed to create dashboard",
            color: 'error',
            timeout: 5000
          });
        }
      } catch (error) {
        console.error("Error creating dashboard:", error);
        this.$store.commit('api/showSnackbar', {
          message: error.response?.data?.message || "Failed to create dashboard",
          color: 'error',
          timeout: 5000
        });
      } finally {
        this.loadingDashboard = false;
      }
    },
    handleDashboardClick(dashboard) {
      // Navigate to the specific dashboard using dashboard name
      // The dashboard object can be either:
      // 1. The mapped object with 'title' property
      // 2. The original dashboard object with 'name' or 'Name' property
      // 3. The mapped object with original dashboard in 'dashboard' property
      let dashboardName = "Untitled Dashboard";
      
      if (dashboard.dashboard) {
        // If original dashboard object is nested
        dashboardName = dashboard.dashboard.name || dashboard.dashboard.Name || dashboard.title || dashboardName;
      } else if (dashboard.name || dashboard.Name) {
        // If it's the original dashboard object
        dashboardName = dashboard.name || dashboard.Name;
      } else if (dashboard.title) {
        // If it's the mapped object with title
        dashboardName = dashboard.title;
      }
      
      // Navigate to dashboard view with name in URL
      this.$router.push({
        name: "AgenticDashboardView",
        params: { dashboardName: dashboardName },
      });
    },
    fetchDashboards() {
      this.loading = true;
      api
        .get("public/api/dashboards", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            (response.data.statusCode === 200 || response.data.statusCode === 201)
          ) {
            const dashboards = response.data.data.dashboards || [];
            // Sort dashboards by creation date (most recent first)
            // Check for CreatedOn, Created, or UpdatedOn fields
            this.allDashboards = dashboards.sort((a, b) => {
              const dateA = a.CreatedOn || a.Created || a.UpdatedOn || a.Updated || a.createdOn || a.created || 0;
              const dateB = b.CreatedOn || b.Created || b.UpdatedOn || b.Updated || b.createdOn || b.created || 0;
              
              // Convert to timestamps for comparison
              const timestampA = dateA ? new Date(dateA).getTime() : 0;
              const timestampB = dateB ? new Date(dateB).getTime() : 0;
              
              // Sort descending (most recent first)
              return timestampB - timestampA;
            });
          } else {
            console.error("Error fetching dashboards:", response?.data?.message);
            this.allDashboards = [];
          }
        })
        .catch((error) => {
          console.error("Error fetching dashboards:", error);
          this.allDashboards = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    checkSidebarState() {
      // Check if sidebar is open by looking at the DOM
      if (this.screenWidth && this.screenWidth >= 768) {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          this.mainSidebarOpen = !sidebar.classList.contains('close');
        } else {
          // Fallback: check after a short delay to ensure DOM is ready
          this.$nextTick(() => {
            const sidebar = document.querySelector('.sidebar');
            if (sidebar) {
              this.mainSidebarOpen = !sidebar.classList.contains('close');
            }
          });
        }
      } else {
        this.mainSidebarOpen = false;
      }
    },
    setupSidebarObserver() {
      // Watch for changes in sidebar class as a fallback
      this.$nextTick(() => {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          this.sidebarObserver = new MutationObserver(() => {
            this.mainSidebarOpen = !sidebar.classList.contains('close');
          });
          this.sidebarObserver.observe(sidebar, {
            attributes: true,
            attributeFilter: ['class']
          });
        }
      });
    },
    handleDrawerStateChange(isOpen) {
      this.mainSidebarOpen = isOpen;
    },
  },
  watch: {
    "$route"(to, from) {
      // Refetch when route changes to update filtered dashboards
      if (
        to.name === "AgenticDashboard" ||
        to.name === "AgenticDashboardAll" ||
        to.name === "AgenticDashboardStarred" ||
        to.name === "AgenticDashboardShared"
      ) {
        this.fetchDashboards();
      }
    },
  },
  mounted() {
    this.fetchDashboards();
    // Check initial sidebar state from DOM
    this.checkSidebarState();
    // Listen for drawer state changes via eventBus
    eventBus.on("mainSidebarStateChanged", this.handleDrawerStateChange);
    
    // Also set up a MutationObserver to watch for sidebar class changes as a fallback
    this.setupSidebarObserver();
  },
  beforeUnmount() {
    eventBus.off("mainSidebarStateChanged", this.handleDrawerStateChange);
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
};
</script>

<style scoped>
@import '../../assets/Styling/agenticDashboard/home.css';
</style>

